/**
 * Structured logging service for CloudIncidents Web Portal
 * Provides consistent logging with context information
 */

export const LogLevel = {
  DEBUG: 'debug',
  INFO: 'info',
  WARN: 'warn',
  ERROR: 'error'
} as const;

export type LogLevel = typeof LogLevel[keyof typeof LogLevel];

interface LogContext {
  [key: string]: unknown;
}

class Logger {
  private isDevelopment = import.meta.env.MODE === 'development';
  private appName = 'CloudIncidents.Web';

  private formatMessage(level: LogLevel, message: string, context?: LogContext): string {
    const timestamp = new Date().toISOString();
    const contextStr = context ? ` | ${JSON.stringify(context)}` : '';
    return `[${timestamp}] [${this.appName}] [${level.toUpperCase()}] ${message}${contextStr}`;
  }

  debug(message: string, context?: LogContext): void {
    if (this.isDevelopment) {
      console.debug(this.formatMessage(LogLevel.DEBUG, message, context));
    }
  }

  info(message: string, context?: LogContext): void {
    console.info(this.formatMessage(LogLevel.INFO, message, context));
  }

  warn(message: string, context?: LogContext): void {
    console.warn(this.formatMessage(LogLevel.WARN, message, context));
  }

  error(message: string, error?: Error | unknown, context?: LogContext): void {
    const errorContext: LogContext = {
      ...context,
      error: error instanceof Error ? {
        message: error.message,
        stack: error.stack,
        name: error.name
      } : error
    };
    console.error(this.formatMessage(LogLevel.ERROR, message, errorContext));
  }

  // Log API requests
  logApiRequest(method: string, url: string, context?: LogContext): void {
    this.debug(`API Request: ${method} ${url}`, context);
  }

  // Log API responses
  logApiResponse(method: string, url: string, status: number, duration?: number): void {
    const context: LogContext = { method, url, status };
    if (duration) {
      context.duration = `${duration}ms`;
    }
    this.info(`API Response: ${method} ${url} - ${status}`, context);
  }

  // Log API errors
  logApiError(method: string, url: string, error: unknown, context?: LogContext): void {
    this.error(`API Error: ${method} ${url}`, error, {
      ...context,
      method,
      url
    });
  }

  // Log user actions
  logUserAction(action: string, context?: LogContext): void {
    this.info(`User Action: ${action}`, context);
  }

  // Log performance metrics
  logPerformance(metric: string, value: number, unit: string = 'ms'): void {
    this.debug(`Performance: ${metric} = ${value}${unit}`, { metric, value, unit });
  }

  // Log authentication events
  logAuth(event: string, context?: LogContext): void {
    this.info(`Auth: ${event}`, context);
  }
}

// Singleton instance
export const logger = new Logger();

// Export for testing/mocking
export default logger;
